/**
 * Mennegat Trading.
 *
 * @author Robuust
 * @author Bob Olde Hampsink <bob@robuust.digital>
 */

import { Controller } from '@hotwired/stimulus';
import * as Turbo from '@hotwired/turbo';

/**
 * Nav controller.
 */
export default class extends Controller {
  static targets = ['submenu', 'button', 'mobilemenu', 'link', 'focus'];

  static classes = ['active', 'open'];

  adapter;

  /**
   * Initialize.
   */
  initialize() {
    this.adapter = Turbo.navigator.delegate.adapter;
  }

  /**
   * Connect.
   */
  connect() {
    document.addEventListener('turbo:before-fetch-request', this.startProgress.bind(this));
    document.addEventListener('turbo:before-fetch-response', this.finishProgress.bind(this));
    document.addEventListener('turbo:before-frame-render', this.reset.bind(this));
  }

  /**
   * Disconnect.
   */
  disconnect() {
    document.removeEventListener('turbo:before-fetch-request', this.startProgress.bind(this));
    document.removeEventListener('turbo:before-fetch-response', this.finishProgress.bind(this));
    document.removeEventListener('turbo:before-frame-render', this.reset.bind(this));
  }

  /**
   * Start Turbo progress bar.
   *
   * @param {Event} e
   */
  startProgress(e) {
    if (!(e.target instanceof HTMLElement)) return;

    this.adapter.formSubmissionStarted(this);

    // Toggle active states
    this.linkTargets.forEach((link) => {
      const isActive = link.pathname !== '/' && e.detail.url.href.includes(link.href);
      link.parentNode.classList.toggle(this.activeClass, isActive);
    });
  }

  /**
   * Finish Turbo progress bar.
   */
  finishProgress() {
    this.adapter.formSubmissionFinished(this);
  }

  /**
   * Toggle the navigation menu.
   *
   * @param {Event} e
   */
  toggle(e) {
    if (this.hasOpenClass) {
      this.buttonTargets.filter((el) => e.target.parentNode !== el.parentNode).forEach((el) => {
        el.parentNode.classList.remove(this.openClass);
      });
      e.target.parentNode.classList.toggle(this.openClass);
    }
    if (this.hasFocusTarget) {
      this.focusTargets.forEach((el) => {
        el.focus();
      });
    }
  }

  /**
   * Toggle levels.
   *
   * @param {Event} e
   */
  level(e) {
    const { level } = e.params;
    this.mobilemenuTarget.classList.remove('level-1', 'level-2', 'level-3');
    this.mobilemenuTarget.classList.add(`level-${level}`);
  }

  /**
   * Reset nav state.
   *
   * @param {Event} e
   */
  reset(e) {
    if (e.detail.newFrame.id === 'main') {
      if (this.hasOpenClass) {
        this.submenuTargets.forEach((el) => {
          el.classList.remove(this.openClass);
        });
      }
      window.scrollTo(0, 0);
    }
  }
}
